import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, FileEdit, Gauge, Save, Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ImageManager } from "@/components/ImageManager";
import { SocialCards } from "@/components/SocialCards";
import { VideoPanel } from "@/components/VideoPanel";
import { CompararNota } from "@/components/CompararNota";
import { PublishTargets } from "@/components/articulo/PublishTargets";
import { NotaTab, SeoTab } from "@/components/articulo/NotaTab";
import { ArticleAside, RedesTab, VistaPrevia } from "@/components/articulo/RedesTab";
import { api, errMsg, fetcher } from "@/lib/api";

const STATUS_LABEL = {
  review: "En revisión", approved: "Aprobada",
  wp_draft: "Borrador en WordPress", published: "Publicada",
};

export default function Articulo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, mutate } = useSWR(`/articles/${id}`, fetcher);
  const { data: outlets } = useSWR("/outlets", fetcher);
  const { data: neighbors } = useSWR(`/articles/${id}/neighbors`, fetcher);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const [sendAt, setSendAt] = useState("");
  const [targets, setTargets] = useState(null);
  const [cardUrl, setCardUrl] = useState("");
  const [oneClick, setOneClick] = useState(false);
  const [socialNets, setSocialNets] = useState(["facebook", "instagram"]);
  const [cardTemplate, setCardTemplate] = useState("");

  useEffect(() => {
    if (data?.id === id && form?.id !== id) {
      setForm(data);
      setTargets(data.outlet_ids || []);
      setCardUrl("");
    }
  }, [data, id, form?.id]);

  // Categorías definidas en los medios de destino de la nota.
  const outletCategories = useMemo(() => {
    const chosen = (targets || []).length ? targets : form?.outlet_ids || [];
    return [...new Set((outlets || [])
      .filter((o) => chosen.includes(o.id))
      .flatMap((o) => o.categories || []))];
  }, [outlets, targets, form?.outlet_ids]);

  if (form?.id !== id) return <p className="text-sm text-muted-foreground">Cargando noticia…</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSeo = (k, v) => setForm((f) => ({ ...f, seo: { ...(f.seo || {}), [k]: v } }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social: { ...(f.social || {}), [k]: v } }));
  const outletId = (targets || [])[0] || (form.outlet_ids || [])[0];

  const act = async (key, fn, msg) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(msg);
      const fresh = await mutate();
      if (fresh) setForm(fresh);
      return res;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const save = () =>
    act("save", () =>
      api.put(`/articles/${id}`, {
        title: form.title, subtitle: form.subtitle, dek: form.dek, excerpt: form.excerpt,
        body_html: form.body_html, category: form.category,
        tags: form.tags, seo: form.seo, social: form.social, whatsapp: form.whatsapp,
        telegram: form.telegram, featured_image: form.featured_image, images: form.images,
        gallery_mode: form.gallery_mode, captions: form.captions || {},
        image_meta: form.image_meta || {},
        secondary_categories: form.secondary_categories || [],
      }), "Cambios guardados");

  const audit = () => act("seo", () => api.post(`/articles/${id}/seo-audit`), "Auditoría SEO completada");
  const approve = () => act("approve", () => api.post(`/articles/${id}/approve`), "Noticia aprobada");
  const go = (nextId) => navigate(`/redaccion/${nextId}`);

  const publish = async (wp_status = "publish", force = false) => {
    if (!(targets || []).length) {
      toast.error("Seleccione al menos un medio de destino");
      return;
    }
    setBusy("publish");
    try {
      const { data: start } = await api.post(`/articles/${id}/publish`, {
        wp_status, force, outlet_ids: targets,
        social_networks: oneClick && wp_status === "publish" ? socialNets : [],
        social_template_id: oneClick ? cardTemplate || null : null,
      });
      if (start.state === "skipped") {
        setBusy("");
        if (window.confirm(`${start.detail}\n\n¿Crear una entrada nueva en WordPress?`)) {
          return publish(wp_status, true);
        }
        return;
      }
      toast.info("Publicando en WordPress… puede tardar hasta dos minutos");
      let attempts = 0;
      const timer = setInterval(async () => {
        attempts += 1;
        try {
          const { data: job } = await api.get(`/articles/${id}/publish-status`);
          if (job.state === "running" && attempts < 60) return;
          clearInterval(timer);
          setBusy("");
          if (job.state === "done") {
            const posted = job.social?.posted || [];
            const failed = job.social?.failed || [];
            if (posted.length) toast.success(`Publicada en redes: ${posted.map((p) => p.network).join(", ")}`);
            failed.forEach((f) => toast.error(`${f.network || "redes"}: ${f.error}`));
            toast.success(wp_status === "publish"
              ? `Publicada en ${(job.results || []).map((r) => r.outlet).join(", ")}`
              : "Enviada como borrador a WordPress");
          } else if (job.state === "error") {
            toast.error(job.errors?.[0]?.error || "No se pudo publicar");
          } else {
            toast.info("El servidor sigue publicando; vuelva a abrir la nota en unos minutos para ver el resultado");
          }
          const fresh = await mutate();
          if (fresh) setForm(fresh);
        } catch (e) {
          clearInterval(timer);
          setBusy("");
          toast.error(errMsg(e));
        }
      }, 4000);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };

  const broadcast = (channels) =>
    act("bc", () =>
      api.post(`/articles/${id}/broadcast`, {
        channels,
        send_at: sendAt ? new Date(sendAt).toISOString() : null,
      }), sendAt ? "Difusión programada" : "Difusión enviada");

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Texto de ${label} copiado`);
    } catch {
      toast.error("No se pudo copiar; seleccione el texto manualmente");
    }
  };

  const insertImageInBody = (url, caption) => {
    const figure = `\n<figure><img src="${url}" alt="${caption || form.title || ""}" />` +
      (caption ? `<figcaption>${caption}</figcaption>` : "") + `</figure>\n`;
    setForm((f) => ({ ...f, body_html: `${f.body_html || ""}${figure}` }));
    toast.success("Imagen insertada al final del cuerpo; recuerde guardar");
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <button
          onClick={() => navigate("/redaccion")}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Volver a la cola
        </button>
        <div className="flex items-center gap-2" data-testid="article-nav">
          <span className="text-xs mono text-muted-foreground">
            {neighbors ? `${neighbors.position} de ${neighbors.total}` : ""}
          </span>
          <Button size="sm" variant="outline" disabled={!neighbors?.prev}
            onClick={() => go(neighbors.prev.id)}
            title={neighbors?.prev?.title || "Sin nota anterior"}
            data-testid="prev-article-btn">
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Anterior
          </Button>
          <Button size="sm" variant="outline" disabled={!neighbors?.next}
            onClick={() => go(neighbors.next.id)}
            title={neighbors?.next?.title || "Sin nota siguiente"}
            data-testid="next-article-btn">
            Siguiente<ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row justify-between gap-4 mb-8">
        <div>
          <div className="kicker text-primary">
            {form.primary_outlet} · {STATUS_LABEL[form.status] || form.status}
          </div>
          <h1 className="text-3xl font-bold tracking-tight mt-1 max-w-3xl">{form.title}</h1>
          {form.subtitle && (
            <p className="serif text-lg text-muted-foreground italic mt-1 max-w-3xl">{form.subtitle}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-start">
          <Button variant="outline" onClick={save} disabled={busy === "save"} data-testid="save-btn">
            <Save className="h-4 w-4 mr-2" />Guardar
          </Button>
          <Button variant="outline" onClick={audit} disabled={busy === "seo"} data-testid="seo-audit-btn">
            <Gauge className="h-4 w-4 mr-2" />{busy === "seo" ? "Auditando…" : "Auditar SEO"}
          </Button>
          <Button variant="outline" onClick={approve} disabled={busy === "approve"} data-testid="approve-btn">
            <CheckCircle2 className="h-4 w-4 mr-2" />Aprobar
          </Button>
          <Button variant="outline" onClick={() => publish("draft")} disabled={busy === "publish"} data-testid="draft-btn">
            <FileEdit className="h-4 w-4 mr-2" />Borrador en WordPress
          </Button>
          <Button onClick={() => publish("publish")} disabled={busy === "publish"} data-testid="publish-btn">
            <Upload className="h-4 w-4 mr-2" />{busy === "publish" ? "Publicando…" : "Publicar"}
          </Button>
        </div>
      </div>

      <PublishTargets
        outlets={outlets} targets={targets} setTargets={setTargets}
        oneClick={oneClick} setOneClick={setOneClick}
        socialNets={socialNets} setSocialNets={setSocialNets}
        cardTemplate={cardTemplate} setCardTemplate={setCardTemplate}
      />

      {(form.pending_checks?.length > 0 || form.compliance_notes?.length > 0) && (
        <div className="border border-primary/40 bg-primary/5 p-5 mb-6" data-testid="compliance-panel">
          <div className="flex items-center gap-2 kicker text-primary mb-2">
            <AlertTriangle className="h-3.5 w-3.5" /> Verificación y cumplimiento
          </div>
          <ul className="text-sm space-y-1 list-disc pl-5">
            {(form.pending_checks || []).map((c) => <li key={`p-${c}`}>{c}</li>)}
            {(form.compliance_notes || []).map((c) => (
              <li key={`c-${c}`} className="text-muted-foreground">{c}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 min-w-0">
          <Tabs defaultValue="nota">
            <TabsList>
              <TabsTrigger value="nota" data-testid="tab-nota">Noticia</TabsTrigger>
              <TabsTrigger value="imagenes" data-testid="tab-imagenes">Imágenes</TabsTrigger>
              <TabsTrigger value="video" data-testid="tab-video">Video</TabsTrigger>
              <TabsTrigger value="seo" data-testid="tab-seo">SEO</TabsTrigger>
              <TabsTrigger value="redes" data-testid="tab-redes">Redes y mensajería</TabsTrigger>
              <TabsTrigger value="comparar" data-testid="tab-comparar">Comparar fuente</TabsTrigger>
              <TabsTrigger value="vista" data-testid="tab-vista">Vista previa</TabsTrigger>
            </TabsList>

            <TabsContent value="nota" className="border border-border bg-card p-6 mt-4">
              <NotaTab form={form} set={set} setForm={setForm} outletCategories={outletCategories} />
            </TabsContent>

            <TabsContent value="imagenes" className="border border-border bg-card p-6 mt-4">
              <div className="kicker mb-1">Galería de la nota</div>
              <p className="text-xs text-muted-foreground mb-5">
                Suba o pegue imágenes, defina la portada, edítelas (rotar y recortar), ordénelas,
                escriba su pie de foto e insértelas dentro del cuerpo. Al publicar se copian a la
                biblioteca de WordPress.
              </p>
              <ImageManager
                articleId={id}
                images={form.images}
                featured={form.featured_image}
                captions={form.captions}
                imageMeta={form.image_meta}
                onChange={(payload) =>
                  setForm((f) => ({
                    ...f,
                    images: payload.images ?? f.images,
                    featured_image: payload.featured_image ?? f.featured_image,
                    captions: payload.captions ?? f.captions,
                    image_meta: payload.image_meta ?? f.image_meta,
                  }))
                }
                onInsert={insertImageInBody}
              />
              <SocialCards
                articleId={id}
                title={form.title}
                category={form.category}
                outlets={outlets}
                outletId={outletId}
                baseImage={form.featured_image}
                onCard={setCardUrl}
              />
            </TabsContent>

            <TabsContent value="video" className="border border-border bg-card p-6 mt-4">
              <VideoPanel
                articleId={id}
                outlets={outlets}
                outletId={outletId}
                videos={form.videos}
                onBody={(html) => setForm((f) => ({ ...f, body_html: html }))}
                onVideos={(list) => setForm((f) => ({ ...f, videos: list }))}
              />
            </TabsContent>

            <TabsContent value="seo" className="border border-border bg-card p-6 mt-4">
              <SeoTab form={form} setSeo={setSeo} seoScore={form.seo?.puntaje || 0} />
            </TabsContent>

            <TabsContent value="redes" className="border border-border bg-card p-6 mt-4">
              <RedesTab
                form={form} set={set} setSocial={setSocial} copy={copy}
                sendAt={sendAt} setSendAt={setSendAt} broadcast={broadcast} busy={busy}
                articleId={id} outlets={outlets} outletId={outletId}
                imageUrl={cardUrl || (form.social_cards || {})["4:5"] || form.featured_image}
              />
            </TabsContent>

            <TabsContent value="comparar" className="border border-border bg-card p-6 mt-4">
              <CompararNota articleId={id} />
            </TabsContent>

            <TabsContent value="vista" className="border border-border bg-card p-6 mt-4">
              <VistaPrevia form={form} />
            </TabsContent>
          </Tabs>
        </div>

        <ArticleAside form={form} set={set} />
      </div>
    </div>
  );
}
